import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AiOutlineCheck, AiOutlineUser } from 'react-icons/ai';
import { FiTruck, FiAlignLeft } from 'react-icons/fi';
import { MdOutlineRefresh } from 'react-icons/md';
import { TbAlignLeft } from 'react-icons/tb';
import { VscSearch } from 'react-icons/vsc';
import { BiBus } from 'react-icons/bi';
import Cookies from 'js-cookie';
import { PageLoadingSpinner } from '../../helpers/loadingSpinner/LoadingSpinner';
import Header from '../../layouts/Header';
import Filter from '../../helpers/filter/Filter';
import CreateLoad from '../../features/creates/load/Load';
import ViewLoad from '../../features/views/load/Load';
import View from '../../helpers/slab/View';
import Create from '../../helpers/panel/Create';
import FilterData from '../../helpers/filter/FilterData';
import ViewCustomer from '../../features/views/customer/Customer';
import ViewLocation from '../../features/views/location/Location';
import ViewDriver from '../../features/views/users/driver/Driver';
import classesGlobal from '../../assets/Global.module.css';
import classesLoad from './Load.module.css';
import classesCustom from '../../helpers/search/CustomSearch.module.css';
import Get from '../../api/internal/Get';
import Input from '../../assets/essentials/Input';
import Select from '../../assets/essentials/Select';
import Button from '../../assets/essentials/Button';
import OptionList from '../../helpers/options/OptionList';
import StatesAndProvinces from '../../helpers/options/StatesAndProvinces';
import ResizableTable from '../../helpers/resizableTable/ResizableTable';
import useBranchList from '../../hooks/useBranchList';
import useDispatchOptions from '../../hooks/useDispatchOptions';
import { BranchContext } from '../../helpers/BranchContext';
import { useUser } from '../../hooks/useUser';
import ColorLegend from './ColorLegend';
import SelectToggle from './SelectToggle';
import { BsCalendar2Week } from 'react-icons/bs';

type BoardType = 'all' | 'dispatch' | 'search' | 'coverage';

function Load({ toggleBoardRefresh }: { toggleBoardRefresh: Function }) {
  const [compactViewOptionOpen, setCompactViewOptionOpen] =
    useState<boolean>(false);
  const [isCompact, setIsCompact] = useState(
    Cookies.get('loadIsCompact') === 'true',
  );
  const [filterValue, setFilterValue] = useState<string>('');
  const [displayedBoard, setDisplayedBoard] = useState<BoardType>('all');
  const [loadData, setLoadData] = useState<LoadBoardItem[]>();
  const [loadDataFilteredBySearch, setLoadDataFilteredBySearch] =
    useState<LoadBoardItem[]>();
  const [loadSearchOptions, setLoadSearchOptions] =
    useState<LoadSearchOptions>();
  const [branchSelectOpen, setBranchSelectOpen] = useState<boolean>(false);
  const [dispatchSelectOpen, setDispatchSelectOpen] = useState<boolean>(false);
  const [selectedDispatchers, setSelectedDispatchers] = useState<string[]>(
    () => {
      const saved = Cookies.get('dispatchers');
      return saved ? saved.split(',').filter(Boolean) : [];
    },
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedBranches, setSelectedBranches] = useContext(BranchContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const branchList = useBranchList();
  const { dispatchOptions } = useDispatchOptions({
    branchIds: selectedBranches,
  });
  const { userDispatchId } = useUser();
  const navigate = useNavigate();

  const GetLoadBoard = useCallback(() => {
    setIsLoading(true);

    const branchQueryString = CreateBranchQueryString(selectedBranches);
    const dispatchQueryString = CreateDispatchQueryString(
      selectedDispatchers,
      displayedBoard,
      userDispatchId,
    );

    Get(`/Load/GetLoadBoard?${dispatchQueryString}${branchQueryString}`)
      .then(response => {
        if (response) {
          setLoadData(response.data.loads);
          setLoadSearchOptions(response.data.loadSearchOptions);
          setIsLoading(false);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedBranches, selectedDispatchers, displayedBoard, userDispatchId]);

  useEffect(() => {
    GetLoadBoard();
  }, [toggleBoardRefresh, GetLoadBoard]);

  function CreateBranchQueryString(values: string[]): string {
    //branchIds are strings due to being stored with cookies
    const intValues = values.map(value => {
      let parsedValue = parseInt(value, 10);
      return isNaN(parsedValue) ? null : parsedValue;
    });
    const validIntValues = intValues.filter(value => value !== null);
    const queryString = validIntValues
      .map(value => `branchId=${value}`)
      .join('&');
    return queryString;
  }

  function CreateDispatchQueryString(
    selectedDispatchers: string[],
    displayedBoard: BoardType,
    userDispatchId: number | null,
  ): string {
    if (displayedBoard !== 'dispatch') {
      return '';
    }

    const allDispatchIds = [...selectedDispatchers];
    if (userDispatchId && !allDispatchIds.includes(String(userDispatchId))) {
      allDispatchIds.push(String(userDispatchId));
    }

    return allDispatchIds.length > 0
      ? allDispatchIds.map(id => `preferredDispatchId=${id}`).join('&') + '&'
      : '';
  }

  const columnConfig = [
    {
      key: '1',
      attribute: 'loadId',
      attributesecondaryhidden: 'loadStatus',
      header: '#',
      onClick: e => View(<ViewLoad loadId={e.target.innerText} />),
      dataType: 'number',
      class: classesLoad.statusBackgroundColor,
    },
    {
      key: '2',
      attribute: 'driverName',
      attributeprimaryhidden: 'employeeId',
      attributesecondaryhidden: 'driverName',
      attributetertiaryhidden: 'dailyLoadStatus',
      header: 'Driver',
      onClick: e =>
        e.target.dataset.attributeprimaryhidden &&
        View(
          <ViewDriver
            driverName={e.target.innerText.split(', ')[0]}
            employeeId={e.target.dataset.attributeprimaryhidden}
          />,
        ),
      class: `${classesLoad.statusBackgroundColor} ${classesLoad.dailyStatusBackgroundColor}`,
    },
    {
      key: '3',
      attribute: 'trailerName',
      header: 'Trailer #',
      isSearchOnly: true,
    },
    {
      key: '4',
      attribute: 'driveClock',
      header: 'Drive Clock',
      isDriverClock: true,
    },
    {
      key: '5',
      attribute: 'shiftClock',
      header: 'Shift Clock',
      isDriverClock: true,
    },
    {
      key: '6',
      attribute: 'cycleClock',
      header: 'Cycle Clock',
      isDriverClock: true,
    },
    {
      key: '7',
      attribute: 'eta',
      header: 'ETA - (ET)',
      isDriverClock: true,
    },
    {
      key: '8',
      attribute: 'customerNames',
      attributeprimaryhidden: 'customerId',
      header: 'Customer',
      onClick: e =>
        e.target.dataset.attributeprimaryhidden &&
        View(
          <ViewCustomer
            customerName={e.target.innerText}
            customerId={e.target.dataset.attributeprimaryhidden}
          />,
        ),
    },
    {
      key: '9',
      attribute: 'reference',
      header: 'Reference',
      attributeprimaryhidden: 'loadId',
      onClick: e =>
        View(<ViewLoad loadId={e.target.dataset.attributeprimaryhidden} />),
    },
    {
      key: '10',
      attribute: 'pickLocation',
      attributeprimaryhidden: 'pickLocationId',
      header: 'Pickup',
      onClick: e =>
        e.target.dataset.attributeprimaryhidden &&
        View(
          <ViewLocation
            locationId={e.target.dataset.attributeprimaryhidden}
            locationName={e.target.innerText}
          />,
        ),
    },
    {
      key: '11',
      attribute: 'pickAppointment',
      header: 'Pickup Appt',
      dataType: 'date',
    },
    {
      key: '12',
      attribute: 'dropLocation',
      attributeprimaryhidden: 'dropLocationId',
      header: 'Drop Off',
      onClick: e =>
        e.target.dataset.attributeprimaryhidden &&
        View(
          <ViewLocation
            locationId={e.target.dataset.attributeprimaryhidden}
            locationName={e.target.innerText}
          />,
        ),
    },
    {
      key: '13',
      attribute: 'dropAppointment',
      header: 'Drop Off Appt',
      dataType: 'date',
    },
    {
      key: '14',
      attribute: 'loadLane',
      header: 'Lane',
    },
    {
      key: '15',
      attribute: 'loadedMiles',
      header: 'Miles',
      dataType: 'formattedNumber',
    },
    {
      key: '16',
      attribute: 'pickDropRatio',
      header: 'Pick/Drop',
    },
    {
      key: '17',
      attribute: 'equipment',
      header: 'Equipment',
    },
    {
      key: '18',
      attribute: 'totalReceivable',
      header: 'Total Receivable',
      dataType: 'currency',
    },
  ];

  const getFilteredColumns = (
    includeDriverClocks = true,
    includeSearchOnly = false,
  ) => {
    return columnConfig.filter(column => {
      if (!includeDriverClocks && column.isDriverClock) {
        return false;
      }
      if (!includeSearchOnly && column.isSearchOnly) {
        return false;
      }
      return true;
    });
  };

  const LoadBoardSection = ({
    title,
    loads,
    filterValue,
    columnConfig,
    compactView,
  }) => {
    return (
      <ResizableTable
        title={title}
        noDataMessage={`No ${title} loads.`}
        data={FilterData(loads, filterValue)}
        columns={columnConfig}
        compactView={compactView}
      />
    );
  };

  const BoardNavButtons = () => {
    const BoardButton = ({
      display,
      setDisplay,
      activeClass,
      condition,
      icon,
      text,
    }) => (
      <button
        onClick={() => setDisplay(display)}
        className={condition ? activeClass : ''}
      >
        {icon}
        <span>{text}</span>
      </button>
    );
    return (
      <div>
        <button
          onClick={() => Create(<CreateLoad RefreshBoard={GetLoadBoard} />)}
        >
          <FiTruck />
          <span>Create a Load</span>
        </button>
        <button onClick={GetLoadBoard}>
          <MdOutlineRefresh />
          <span>Refresh Board</span>
        </button>
        <div className={classesGlobal.toggleDisplayHolder}>
          <BoardButton
            display="all"
            setDisplay={setDisplayedBoard}
            activeClass={classesGlobal.buttonActive}
            condition={displayedBoard === 'all'}
            icon={<FiTruck />}
            text="All Loads"
          />
          <BoardButton
            display="coverage"
            setDisplay={setDisplayedBoard}
            activeClass={classesGlobal.buttonActive}
            condition={displayedBoard === 'coverage'}
            icon={<AiOutlineUser />}
            text="Coverage"
          />
          <BoardButton
            display="dispatch"
            setDisplay={setDisplayedBoard}
            activeClass={classesGlobal.buttonActive}
            condition={displayedBoard === 'dispatch'}
            icon={<BiBus />}
            text="Dispatch"
          />
          <button onClick={() => navigate('/user/schedule/view')}>
            <BsCalendar2Week title="Schedule" />
            Schedule
          </button>
          <BoardButton
            display="search"
            setDisplay={setDisplayedBoard}
            activeClass={classesGlobal.buttonActive}
            condition={displayedBoard === 'search'}
            icon={<VscSearch />}
            text="Search Load"
          />
        </div>
      </div>
    );
  };

  const StatusBoard = ({ loads }) => {
    const futureLoads = loads?.filter(load => load.sortStatus === 'Future');
    const needsActionLoads = loads?.filter(
      load => load.sortStatus === 'Needs Action',
    );
    //requested to display by load status, pick date sort coming from backend
    const otrLoads = loads
      ?.filter(load => load.sortStatus === 'Over The Road')
      .sort((a, b) => a.loadStatusId - b.loadStatusId);

    return (
      <>
        <LoadBoardSection
          title="Future"
          loads={futureLoads || []}
          filterValue={filterValue}
          columnConfig={getFilteredColumns(false)}
          compactView={isCompact}
        />
        <LoadBoardSection
          title="Needs Action"
          loads={needsActionLoads || []}
          filterValue={filterValue}
          columnConfig={getFilteredColumns(true)}
          compactView={isCompact}
        />
        <LoadBoardSection
          title="Over The Road"
          loads={otrLoads || []}
          filterValue={filterValue}
          columnConfig={getFilteredColumns(true)}
          compactView={isCompact}
        />
      </>
    );
  };

  const DisplayByCoveredBoard = () => {
    const unassignedLoads = loadData?.filter(load => !load.isAssigned);
    return (
      <LoadBoardSection
        title="Needs Covered"
        loads={unassignedLoads || []}
        filterValue={filterValue}
        columnConfig={getFilteredColumns(false)}
        compactView={isCompact}
      />
    );
  };

  const LoadSearchBoard = () => {
    function SearchLoad(e) {
      setIsLoading(true);
      e.preventDefault();
      setFilterValue('');
      const formData = new FormData(e.target);
      const newParams = new URLSearchParams();
      formData.forEach((value, key) => {
        if (value !== '') {
          newParams.set(key, value.toString());
        }
      });
      setSearchParams(newParams, { replace: true });
      Get(`/Load/SearchLoad?${newParams.toString()}`)
        .then(response => {
          if (response) {
            setLoadDataFilteredBySearch(response.data);
            setIsLoading(false);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    function HandleCancelSearch() {
      setDisplayedBoard('all');
      setLoadDataFilteredBySearch(null);
      setSearchParams({});
    }

    const SearchForm = () => {
      return (
        <form onSubmit={e => SearchLoad(e)}>
          <label>
            Customer
            <Input
              name="customer"
              defaultValue={searchParams.get('customer') ?? ''}
            />
          </label>
          <label>
            Vendor
            <Input
              name="vendor"
              defaultValue={searchParams.get('vendor') ?? ''}
            />
          </label>
          <label>
            Driver
            <Input
              name="driver"
              defaultValue={searchParams.get('driver') ?? ''}
            />
          </label>
          <label>
            Reference
            <Input
              name="reference"
              defaultValue={searchParams.get('reference') ?? ''}
            />
          </label>
          <label>
            Equipment
            <Select
              name="equipmentTypeId"
              defaultValue={searchParams.get('equipmentTypeId') ?? ''}
            >
              <option value="" />
              <OptionList
                optionData={loadSearchOptions?.equipmentOptions}
                attributeID="equipmentTypeId"
                attributeName="type"
              />
            </Select>
          </label>
          <label>
            Commodity
            <Select
              name="commodityId"
              defaultValue={searchParams.get('commodityId') ?? ''}
            >
              <option value="" />
              <OptionList
                optionData={loadSearchOptions?.commodityOptions}
                attributeID="commodityId"
                attributeName="type"
                attributeGroup="group"
              />
            </Select>
          </label>
          <label>
            Branch
            <Select
              name="branchId"
              defaultValue={searchParams.get('branchId') ?? ''}
            >
              <option value="" />
              <OptionList
                optionData={loadSearchOptions?.branchOptions}
                attributeID="branchId"
                attributeName="branchName"
              />
            </Select>
          </label>
          <label>
            Status
            <Select
              name="loadStatusId"
              defaultValue={searchParams.get('loadStatusId') ?? ''}
            >
              <option value="">Any Status</option>
              <option value="0">Displayed On Load Board</option>
              <OptionList
                optionData={loadSearchOptions?.loadStatusOptions}
                attributeID="loadStatusId"
                attributeName="status"
                attributeGroup="group"
              />
            </Select>
          </label>
          <label>
            Picks
            <Input
              type="number"
              step="1"
              name="picksCount"
              defaultValue={searchParams.get('picksCount') ?? ''}
            />
          </label>
          <label>
            Drops
            <Input
              type="number"
              step="1"
              name="dropsCount"
              defaultValue={searchParams.get('dropsCount') ?? ''}
            />
          </label>
          <label>
            Location
            <Input
              name="location"
              defaultValue={searchParams.get('location') ?? ''}
            />
          </label>
          <label>
            Origin City
            <Input
              name="originCity"
              defaultValue={searchParams.get('originCity') ?? ''}
            />
          </label>
          <label>
            Origin State
            <Select
              name="originState"
              defaultValue={searchParams.get('originState') ?? ''}
            >
              <option value="" />
              <StatesAndProvinces />
            </Select>
          </label>
          <label>
            Destination City
            <Input
              name="destinationCity"
              defaultValue={searchParams.get('destinationCity') ?? ''}
            />
          </label>
          <label>
            Destination State
            <Select
              name="destinationState"
              defaultValue={searchParams.get('destinationState') ?? ''}
            >
              <option value="" />
              <StatesAndProvinces />
            </Select>
          </label>
          <label>
            Stop PO
            <Input name="po" defaultValue={searchParams.get('po') ?? ''} />
          </label>
          <label>
            Trailer #
            <Input
              name="trailerName"
              defaultValue={searchParams.get('trailerName') ?? ''}
            />
          </label>
          <div className={classesCustom.separateRow}>
            <div className={classesCustom.dateRange}>
              <span className={classesGlobal.span2}>Pickup Date</span>
              <label htmlFor="pickupDateMin">Start: </label>
              <Input
                id="pickupDateMin"
                type="date"
                name="pickupDateMin"
                defaultValue={searchParams.get('pickupDateMin') ?? ''}
              />
              <label htmlFor="pickupDateMax">End: </label>
              <Input
                id="pickupDateMax"
                type="date"
                name="pickupDateMax"
                defaultValue={searchParams.get('pickupDateMax') ?? ''}
              />
            </div>
            <div className={classesCustom.dateRange}>
              <span className={classesGlobal.span2}>Delivery Date</span>
              <label htmlFor="deliveryDateMin">Start: </label>
              <Input
                id="deliveryDateMin"
                type="date"
                name="deliveryDateMin"
                defaultValue={searchParams.get('deliveryDateMin') ?? ''}
              />
              <label htmlFor="deliveryDateMax">End: </label>
              <Input
                id="deliveryDateMax"
                type="date"
                name="deliveryDateMax"
                defaultValue={searchParams.get('deliveryDateMax') ?? ''}
              />
            </div>
          </div>
          <div className={classesCustom.submitHolder}>
            <Button type="submit" variant="good">
              Search
            </Button>
            <Button
              variant="bad"
              type="button"
              onClick={() => HandleCancelSearch()}
            >
              Cancel
            </Button>
          </div>
        </form>
      );
    };

    return (
      <>
        <div className={classesCustom.customSearch}>
          <SearchForm />
        </div>{' '}
        <LoadBoardSection
          title="Search Result"
          loads={loadDataFilteredBySearch || []}
          filterValue={filterValue}
          columnConfig={getFilteredColumns(false, true)}
          compactView={isCompact}
        />
      </>
    );
  };

  const CompactViewToggle = () => {
    return (
      <div className={classesGlobal.dropdown}>
        <button
          aria-label="Open Toggle Compact Menu"
          title="Toggle Compact Menu"
          onClick={() => setCompactViewOptionOpen(!compactViewOptionOpen)}
        >
          {isCompact ? <FiAlignLeft /> : <TbAlignLeft />}
        </button>
        {compactViewOptionOpen && (
          <div className={classesGlobal.dropdownOptions}>
            <div
              onClick={() => {
                setIsCompact(false);
                Cookies.set('loadIsCompact', 'false');
                setCompactViewOptionOpen(false);
              }}
            >
              {!isCompact ? <AiOutlineCheck /> : <div />}
              <TbAlignLeft />
              <span>Normal View</span>
            </div>
            <div
              onClick={() => {
                setIsCompact(true);
                Cookies.set('loadIsCompact', 'true');
                setCompactViewOptionOpen(false);
              }}
            >
              {isCompact ? <AiOutlineCheck /> : <div />}
              <FiAlignLeft />
              <span>Compact View</span>
            </div>
          </div>
        )}
      </div>
    );
  };
  return (
    <>
      <h1>Load</h1>
      <Header>
        <BoardNavButtons />
        <div>
          <SelectToggle
            title="Branch"
            isOpen={branchSelectOpen}
            setIsOpen={setBranchSelectOpen}
            selectedItems={selectedBranches}
            setSelectedItems={setSelectedBranches}
            options={branchList}
            cookieKey="branches"
            idKey="branchId"
            nameKey="branchName"
          />
          {displayedBoard === 'dispatch' && (
            <SelectToggle
              title="Dispatch"
              isOpen={dispatchSelectOpen}
              setIsOpen={setDispatchSelectOpen}
              selectedItems={selectedDispatchers}
              setSelectedItems={setSelectedDispatchers}
              options={dispatchOptions}
              cookieKey="dispatchers"
              idKey="dispatchId"
              nameKey="dispatchName"
              defaultSelectId={userDispatchId}
            />
          )}
          <ColorLegend />
          <CompactViewToggle />
          <Filter
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            placeholder="Filter current loads"
          />
        </div>
      </Header>
      {isLoading ? (
        <PageLoadingSpinner />
      ) : (
        <>
          {displayedBoard === 'all' && (
            <StatusBoard key="all" loads={loadData} />
          )}
          {displayedBoard === 'coverage' && <DisplayByCoveredBoard />}
          {displayedBoard === 'dispatch' && (
            <StatusBoard key="dispatch" loads={loadData} />
          )}
          {displayedBoard === 'search' && <LoadSearchBoard />}
        </>
      )}
    </>
  );
}

export default Load;
